// 剣舞「幕引きの一閃」の作り直し＝手拍子の2撃 (配布版の既定・前の一礼→一閃は DEV ?bladeFinisher=v1)。
//
// 作者の原文 (10-31): 「いつ来るのかわかりにくい攻撃にしないといけない。フェイントのようなものも入れてほしいし…一発じゃなくてもいい」
// 作者の答え (10-31): Q3「ア 手拍子」。手触り「リズムが違うのは、なんか理不尽さがあるから、リズムは一定にして、
//   2回目に来るときと3回目に来るときと4回目に来るときがあるっていう風にした方が、まだいいかな。」
// 設計 = docs/research/2026-10-31-exec-bait-and-blade-finisher.md の「ア 手拍子」。剣舞の芯「構えで次のリズムを知らせる」を致命にも通す。
//   1. 手を 2〜4 回打つ。拍の速さはいつも同じ (520ms)。何回打つかが回ごとに違う (数えて当てることはできない)。紫は無し。
//   2. フェイント (半分の回): どこかの拍で手を上げたまま打たずに止まる (一拍の休み・拍の刻みは崩さない)。音も紫も無し。
//   3. 次の拍で両の剣を V 字に掲げる＝ここで初めて紫と一瞬の止め。ここからは同じ拍で 斬り → 返し斬り の2撃。
//      読み方は「開いた所から、その回の拍で 1・2」。手拍子の数を数えても当たらない。
//   4. どちらかを弾き損ねたら致命が当たる (2撃目は出ない)。2撃とも弾いて初めて「弾いた」(ボスの体幹・押し戻し)。
// 新しい絵は作っていない (拍手の3枚・V 字の構え・滑り・斬り抜け・横薙ぎは今ある絵)。
// URL を読むのは game.ts の DEV の箱の中。

import type { AttackDef } from './types'
import type { BossAttackTimeline } from './boss_pose_timeline'
import { EXEC_PARRY } from './exec_parry'

/** 拍の速さ (いつも同じ・10-31 作者「リズムは一定」)。 */
export const BLADE_FINISHER_TEMPO_MS = 520
export const BLADE_FINISHER_CLAPS = [2, 3, 4] as const
/** 崩れてから最初の拍までの入り (手を胸へ上げる)。 */
export const BLADE_FINISHER_LEAD_MS = 350
/** 1撃の当たりの長さ。2撃目は1撃目の当たりの頭からちょうど1拍後。 */
export const BLADE_FINISHER_ACTIVE_MS = 120
/** 1撃目を受け止めた時の止め (ms)。致命を弾いた時より短く、2撃目の拍を崩さない。 */
export const BLADE_FINISHER_CATCH_STOP_MS = 70
/** フェイント (打たない拍) が入る割合。 */
export const BLADE_FINISHER_FEINT_ODDS = 0.5

export type BladeClapBeat = 'clap' | 'rest'
export type BladeClapPlan = {
  tempoMs: number
  beats: BladeClapBeat[]
  /** 腕を開く (紫・止め) 時刻 (溜めの頭から ms)。 */
  openAtMs: number
  chargeMs: number
}

let on = true
/** 指定なし (null) は配布版の手拍子。v1 で前の一礼→一閃。 */
export function setBladeFinisher(value: string | null | undefined): boolean {
  on = value !== 'v1'
  return on
}
export function bladeFinisherV2On(): boolean { return on }

export function planBladeClap(rand: () => number = Math.random): BladeClapPlan {
  const tempoMs = BLADE_FINISHER_TEMPO_MS
  const claps = BLADE_FINISHER_CLAPS[Math.floor(rand() * BLADE_FINISHER_CLAPS.length)]!
  const beats: BladeClapBeat[] = Array.from({ length: claps }, () => 'clap')
  // 休みは最初の拍には置かない (打ち始めを見せてから崩す)。最後の拍の後 (開くと見せて開かない) もあり。
  if (rand() < BLADE_FINISHER_FEINT_ODDS) beats.splice(1 + Math.floor(rand() * claps), 0, 'rest')
  const openAtMs = BLADE_FINISHER_LEAD_MS + beats.length * tempoMs
  // 開いた時の一瞬の止め (signalFreezeMs) を拍に含める＝開いてから1撃目まで、画面の上でちょうど1拍。
  return { tempoMs, beats, openAtMs, chargeMs: openAtMs + tempoMs - EXEC_PARRY.signalFreezeMs }
}

/** 致命の写しにこの回の段取りを載せる (2撃・2撃目は1拍後)。 */
export function bladeClapAttack(base: AttackDef, rand: () => number = Math.random): AttackDef {
  const plan = planBladeClap(rand)
  return {
    ...base,
    chargeMs: plan.chargeMs,
    activeMs: BLADE_FINISHER_ACTIVE_MS,
    comboHits: 2,
    // 1撃目を受け止めた止めを拍に含める＝弾いた時、1撃目から2撃目まで画面の上でちょうど1拍。
    comboGapMs: plan.tempoMs - BLADE_FINISHER_ACTIVE_MS - BLADE_FINISHER_CATCH_STOP_MS,
    bladeClap: plan,
  }
}

/** 拍の時刻 (溜めの頭から ms)。 */
export function bladeClapBeatMs(plan: BladeClapPlan, i: number): number {
  return BLADE_FINISHER_LEAD_MS + i * plan.tempoMs
}

/** この tick で跨いだ手拍子の数 (音を鳴らす)。休みの拍は鳴らない。 */
export function bladeClapsCrossed(plan: BladeClapPlan, prevMs: number, nowMs: number): number {
  let n = 0
  plan.beats.forEach((b, i) => {
    const t = bladeClapBeatMs(plan, i)
    if (b === 'clap' && prevMs < t && nowMs >= t) n++
  })
  return n
}

/** 合図 (紫が灯る・一瞬止める) を、この tick で跨いだか。 */
export function bladeClapSignalCrossed(plan: BladeClapPlan, prevMs: number, nowMs: number): boolean {
  return prevMs < plan.openAtMs && nowMs >= plan.openAtMs
}

/** 溜めの間の紫の強さ。開くまでは無し (null)、開いたら一気に灯って当たりへ向けて濃く。 */
export function bladeClapAuraK(plan: BladeClapPlan, elapsedMs: number): number | null {
  if (elapsedMs < plan.openAtMs) return null
  return 0.75 + 0.25 * Math.min(1, (elapsedMs - plan.openAtMs) / plan.tempoMs)
}

const CLAP_LIFT = 'm1029_clap_in'   // 手を胸へ上げる途中
const CLAP_OPEN = 'm1025_k_1'       // 手を開いた
const CLAP_SHUT = 'm1025_k_2'       // 手を打ち合わせた
const V = (k: string) => `m1020_v6_${k}`
const cache = new WeakMap<BladeClapPlan, BossAttackTimeline>()

/** この回の並び (溜め = 拍手と休み → V 字 → 滑り / 1撃目 = 斬り抜け / 2撃目 = 横薙ぎ)。 */
export function bladeClapTimeline(plan: BladeClapPlan): BossAttackTimeline {
  const hit = cache.get(plan)
  if (hit) return hit
  const C = plan.chargeMs
  const at = (ms: number) => Math.max(0, Math.min(0.999, ms / C))
  const out: BossAttackTimeline = [{ phase: 'telegraph', at: 0, image: CLAP_LIFT, hold: true, tag: 'anticipation', ease: 'easeIn' }]
  const open = Math.min(140, plan.tempoMs * 0.3)
  plan.beats.forEach((b, i) => {
    const t = bladeClapBeatMs(plan, i)
    out.push({ phase: 'telegraph', at: at(t - open), image: CLAP_OPEN, hold: true })
    if (b === 'clap') out.push({ phase: 'telegraph', at: at(t), image: CLAP_SHUT, hold: true })
    // 休み: 開いた手のまま止まる (少し沈んで「打つ」を溜めて打たない)。
    else out.push({ phase: 'telegraph', at: at(t), image: CLAP_OPEN, hold: true, scale: { y: 0.97 }, ease: 'step' })
  })
  out.push(
    { phase: 'telegraph', at: at(plan.openAtMs), image: V('c4'), hold: true, ease: 'step' },
    { phase: 'telegraph', at: at(plan.chargeMs - plan.tempoMs * 0.3), image: 'glide', hold: true },
    { phase: 'active', at: 0, image: 'exec_slash', hold: true, tag: 'contact', hitIndex: 0 },
    { phase: 'comboGap', at: 0, image: 'exec_slash', hold: true, tag: 'followThrough', hitIndex: 0 },
    { phase: 'comboGap', at: 0.45, image: V('e2'), hold: true, hitIndex: 0 },
    { phase: 'active', at: 0, image: V('e3'), hold: true, tag: 'contact', hitIndex: 1 },
    { phase: 'recovery', at: 0, image: V('e4'), hold: true, tag: 'followThrough' },
    { phase: 'recovery', at: 0.5, image: 'm1025_w_3', hold: true, tag: 'recover' },
    { phase: 'recovery', at: 0.85, image: 'idle', hold: true },
  )
  cache.set(plan, out)
  return out
}

/** 手拍子の音 (既定は専用の1拍 = src/blade_sfx_v2.ts・11-04 作者採用・DEV ?bladeSfx=v1 は ADV の拍手の音を1拍ずつ借りる)。 */
export { bladeFinisherClapSfx as bladeClapSfx } from './blade_sfx_v2'
